import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';

import { CatalogContainer } from '../../components/Containers/CatalogContainer.component';
import { OrderSummaryContainer } from '../../components/Containers/OrderSummaryContainer.component';
import { DeliveryInfoSection } from '../../components/Containers/DeliveryInfoSection.component';
import { DeliveryCostSection } from '../../components/Containers/DeliveryCostSection.component';
import { OrderActionsSection } from '../../components/Containers/OrderActionsSection.component';
import { useOrderFormStore } from '../../store/order-form.store';
import { OrderFormState } from '../../utils/order-to-form-state.util';
import { Order } from '../../types/order.type';
import { DeliveryAddress, DeliveryDay, DeliveryType, UserAddress } from '../../types/delivery-selection.type';
import { GlobalDiscount } from '../../../quotes/utils/compute-totals-local.util';

type Props = {
  mode: 'create' | 'update';
  title: string;
  ctaLabel: string;

  addresses: UserAddress[];
  lastOrder?: Order | null;
  defaultOrganizationId?: string;
  totalsDiscount?: { companyDiscountPercent?: number; globalDiscount?: GlobalDiscount };

  initialValues: OrderFormState;
  onSubmit: (currentState: OrderFormState) => Promise<void>;
  isSubmitting: boolean;
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',

    '& h4': {
      margin: 0,
      opacity: 0.7,
    },
  },

  header: {
    '& > h2': {
      margin: 0,
    },
  },

  bottom: {
    display: 'grid',
    gridTemplateColumns: '3fr 0.5fr 1fr',
    gap: '1rem',
  },
});

export function OrderFormView({
  title,
  ctaLabel,
  addresses,
  lastOrder,
  defaultOrganizationId,
  totalsDiscount,
  initialValues,
  onSubmit,
  isSubmitting,
}: Props) {
  const classes = useStyles();

  const [
    items,
    addProduct,
    addBasket,
    addVirtual,
    increaseQuantity,
    decreaseQuantity,
    changeQuantity,
    initialize,
    reset,
  ] = useOrderFormStore((state) => [
    state.items,
    state.addProduct,
    state.addBasket,
    state.addVirtual,
    state.increaseQuantity,
    state.decreaseQuantity,
    state.changeQuantity,
    state.initialize,
    state.reset,
  ]);

  const [selectedDeliveryType, setSelectedDeliveryType] = useState<DeliveryType>(initialValues.deliveryType);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<DeliveryAddress | null>(
    initialValues.deliveryAddress
  );
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<DeliveryAddress | null>(
    initialValues.billingAddress
  );
  const [selectedDeliveryDay, setSelectedDeliveryDay] = useState<DeliveryDay | null>(initialValues.deliveryDay);
  const [selectedDeliveryPointId, setSelectedDeliveryPointId] = useState<string | null>(initialValues.deliveryPointId);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(initialValues.organizationId);
  const [selectedStatus, setSelectedStatus] = useState(initialValues.status);
  const [selectedAdvStatus, setSelectedAdvStatus] = useState(initialValues.advStatus);
  const [selectedDeliveryCost, setSelectedDeliveryCost] = useState<number>(initialValues.deliveryCost);

  useEffect(() => {
    initialize(initialValues.items);
    return () => reset();
  }, []);

  const isMissingOrganizationDeliveryPoint = selectedDeliveryType === 'organization' && !selectedDeliveryPointId;
  const canSubmit =
    items.length > 0 && !!selectedDeliveryAddress && !!selectedDeliveryDay && !isMissingOrganizationDeliveryPoint;

  const handleSubmit = () => {
    onSubmit({
      items,
      deliveryType: selectedDeliveryType,
      deliveryAddress: selectedDeliveryAddress,
      billingAddress: selectedBillingAddress,
      deliveryDay: selectedDeliveryDay,
      deliveryPointId: selectedDeliveryPointId,
      organizationId: selectedOrganizationId,
      status: selectedStatus,
      advStatus: selectedAdvStatus,
      deliveryCost: selectedDeliveryCost,
    });
  };

  return (
    <section className={classes.root}>
      <section className={classes.header}>
        <h2>{title}</h2>
      </section>
      <CatalogContainer onAddProduct={addProduct} onAddBasket={addBasket} onAddVirtual={addVirtual} />
      <OrderSummaryContainer
        items={items}
        onIncrease={increaseQuantity}
        onDecrease={decreaseQuantity}
        onChangeQuantity={changeQuantity}
        {...(totalsDiscount && {
          companyDiscountPercent: totalsDiscount.companyDiscountPercent,
          globalDiscount: totalsDiscount.globalDiscount,
          deliveryCost: selectedDeliveryCost,
        })}
      />
      <section className={classes.bottom}>
        <DeliveryInfoSection
          addresses={addresses}
          lastOrder={lastOrder ?? null}
          defaultOrganizationId={defaultOrganizationId}
          selectedDeliveryType={selectedDeliveryType}
          selectedDeliveryAddress={selectedDeliveryAddress}
          selectedBillingAddress={selectedBillingAddress}
          selectedDeliveryDay={selectedDeliveryDay}
          selectedDeliveryPointId={selectedDeliveryPointId}
          onDeliveryTypeChange={setSelectedDeliveryType}
          onDeliveryAddressChange={setSelectedDeliveryAddress}
          onBillingAddressChange={setSelectedBillingAddress}
          onDeliveryDayChange={setSelectedDeliveryDay}
          onDeliveryPointChange={setSelectedDeliveryPointId}
          onOrganizationChange={setSelectedOrganizationId}
        />
        <DeliveryCostSection value={selectedDeliveryCost} onChange={setSelectedDeliveryCost} />
        <OrderActionsSection
          status={selectedStatus}
          advStatus={selectedAdvStatus}
          onStatusChange={setSelectedStatus}
          onAdvStatusChange={setSelectedAdvStatus}
          onSubmit={handleSubmit}
          canSubmit={canSubmit}
          submitLabel={ctaLabel}
          isSubmitting={isSubmitting}
        />
      </section>
    </section>
  );
}
